import merge from 'lodash.merge';
import { palette } from './palette';
import { typography } from './typography';
import { color, colorsIdsToHexVals, colorMapToHexVals, lightCategoricalColor, darkCategoricalColor, } from './colors';
import { shape } from './shape';
import { spacing } from './spacing';
export const theme = {
    background: palette.common.transparent.hex,
    fontFamily: typography.font.family.sansSerif,
    fontSize: parseFloat(typography.textStyles.micro.fontSize) * 16,
    textColor: palette.text.primary.hex,
    axis: {
        domain: {
            line: {
                stroke: palette.common.transparent.hex,
                strokeWidth: 1,
            },
        },
        ticks: {
            line: {
                stroke: palette.border.dark.hex,
                strokeWidth: 1,
            },
            text: {},
        },
        legend: {
            text: {
                fontSize: parseFloat(typography.textStyles.normal.fontSize) * 16,
                fontWeight: typography.font.weight.bold,
            },
        },
    },
    grid: {
        line: {
            stroke: palette.border.default.hex,
            strokeWidth: 1,
        },
    },
    legends: {
        hidden: {
            symbol: {
                fill: color.N9.hex,
                opacity: 0.6,
            },
            text: {
                fill: color.N9.hex,
                opacity: 0.6,
            },
        },
        text: {
            fill: color.N10.hex,
        },
    },
    labels: {
        text: {
            fontSize: parseFloat(typography.textStyles.small.fontSize) * 16,
        },
    },
    markers: {
        lineColor: palette.common.black.hex,
        lineStrokeWidth: 1,
        text: {},
    },
    dots: {
        text: {},
    },
    tooltip: {
        container: {
            background: palette.background.light.hex,
            color: 'inherit',
            fontSize: 'inherit',
            borderRadius: shape.borderRadius.default,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
            padding: `${spacing.xs2} ${spacing.xs}`,
        },
        basic: {
            whiteSpace: 'pre',
            display: 'flex',
            alignItems: 'center',
        },
        chip: {
            marginRight: spacing.xs.getValue(),
        },
        table: {},
        tableCell: {
            padding: '3px 5px',
        },
        tableCellValue: {
            fontWeight: typography.font.weight.bold,
        },
    },
    crosshair: {
        line: {
            stroke: palette.common.black.hex,
            strokeWidth: 1,
            strokeOpacity: 0.75,
            strokeDasharray: '6 6',
        },
    },
    annotations: {
        text: {
            fontSize: parseFloat(typography.textStyles.small.fontSize) * 16,
            outlineWidth: 2,
            outlineColor: palette.common.white.hex,
        },
        link: {
            stroke: palette.common.black.hex,
            strokeWidth: 1,
            outlineWidth: 2,
            outlineColor: palette.common.white.hex,
        },
        outline: {
            fill: 'none',
            stroke: palette.common.black.hex,
            strokeWidth: 2,
            outlineWidth: 2,
            outlineColor: palette.common.white.hex,
        },
        symbol: {
            fill: palette.common.black.hex,
            outlineWidth: 2,
            outlineColor: palette.common.white.hex,
        },
    },
};
export const themeDark = merge({}, theme, {
    textColor: palette.text.contrast.hex,
    axis: {
        ticks: {
            line: {
                opacity: 0.15,
            },
        },
    },
    grid: {
        line: {
            opacity: 0.15,
        },
    },
    legends: {
        hidden: {
            symbol: {
                fill: color.N3.hex,
                opacity: 0.6,
            },
            text: {
                fill: color.N3.hex,
                opacity: 0.6,
            },
        },
        text: {
            fill: color.N2.hex,
        },
    },
    markers: {
        lineColor: palette.common.white.hex,
    },
    crosshair: {
        line: {
            stroke: palette.common.white.hex,
        },
    },
    annotations: {
        text: {
            outlineColor: palette.common.black.hex,
        },
        link: {
            stroke: palette.common.white.hex,
            outlineColor: palette.common.black.hex,
        },
        outline: {
            stroke: palette.common.white.hex,
            outlineColor: palette.common.black.hex,
        },
        symbol: {
            fill: palette.common.white.hex,
            outlineColor: palette.common.black.hex,
        },
    },
});
// these are used by more than 1 component
const generalDefaults = {
    useMesh: true,
    lightColorHexes: colorMapToHexVals(lightCategoricalColor),
    darkColorHexes: colorMapToHexVals(darkCategoricalColor),
    lightValueLabelColor: palette.text.contrast.hex,
    darkValueLabelColor: palette.text.primary.hex,
};
// defaults for specific components
export const lineDefaults = {
    theme,
    lineWidth: 4,
    pointSize: 8,
    curve: 'natural',
    useMesh: generalDefaults.useMesh,
    colors: generalDefaults.lightColorHexes,
};
export const lineDefaultsDark = merge({}, lineDefaults, {
    theme: themeDark,
    colors: generalDefaults.darkColorHexes,
});
export const pieDefaults = {
    theme,
    innerRadius: 0.5,
    padAngle: 1.5,
    cornerRadius: parseFloat(shape.borderRadius.default),
    activeOuterRadiusOffset: spacing.xs.getValue(),
    arcLinkLabelsSkipAngle: 10,
    arcLinkLabelsThickness: 2,
    arcLabelsSkipAngle: 25,
    arcLinkLabelsColor: {
        from: 'color',
    },
    arcLabelsTextColor: generalDefaults.lightValueLabelColor,
    colors: generalDefaults.lightColorHexes,
};
export const pieDefaultsDark = merge({}, pieDefaults, {
    theme: themeDark,
    colors: generalDefaults.darkColorHexes,
    arcLabelsTextColor: generalDefaults.darkValueLabelColor,
});
export const heatmapDefaults = {
    theme,
    padding: 2,
    labelTextColor: generalDefaults.lightValueLabelColor,
    hoverTarget: 'cell',
    colors: colorsIdsToHexVals(['B6', 'B7', 'B8', 'B9', 'B10']),
};
export const heatmapDefaultsDark = merge({}, heatmapDefaults, {
    theme: themeDark,
    labelTextColor: generalDefaults.darkValueLabelColor,
    colors: colorsIdsToHexVals(['B1', 'B2', 'B3', 'B4', 'B5']),
});
export const barDefaults = {
    theme,
    padding: 0.3,
    borderColor: {
        from: 'color',
        modifiers: [['darker', 1.6]],
    },
    labelSkipWidth: 12,
    labelSkipHeight: 15,
    labelTextColor: generalDefaults.lightValueLabelColor,
    colors: generalDefaults.lightColorHexes,
};
export const barDefaultsDark = merge({}, barDefaults, {
    theme: themeDark,
    borderColor: {
        from: 'color',
        modifiers: [['brighter', 1.6]],
    },
    colors: generalDefaults.darkColorHexes,
    labelTextColor: generalDefaults.darkValueLabelColor,
});
export const bulletDefaults = {
    theme,
    titleOffsetX: -spacing.sm.getValue(),
    markerSize: 1.6,
    measureSize: 0.3,
    titleAlign: 'end',
    rangeColors: colorsIdsToHexVals(['N4', 'N5', 'N6', 'N7', 'N8', 'N9']),
    measureColors: colorsIdsToHexVals(['B5', 'B6', 'B7', 'B8']),
    markerColors: colorsIdsToHexVals(['G6', 'G7', 'G9', 'G10']),
};
export const bulletDefaultsDark = merge({}, bulletDefaults, {
    theme: themeDark,
});
export const scatterplotDefaults = {
    theme,
    nodeSize: 12,
    useMesh: generalDefaults.useMesh,
    blendMode: 'multiply',
    colors: generalDefaults.lightColorHexes,
};
export const scatterplotDefaultsDark = merge({}, scatterplotDefaults, {
    theme: themeDark,
    blendMode: 'normal',
    colors: generalDefaults.darkColorHexes,
});
export const patterns = {
    dots: {
        id: 'dots',
        type: 'patternDots',
        size: 2,
        padding: 2,
        stagger: false,
        background: color.transparent.hex,
        color: 'inherit',
    },
    lines: {
        id: 'lines',
        type: 'patternLines',
        spacing: 5,
        rotation: 45,
        lineWidth: 2,
        background: color.transparent.hex,
        color: 'inherit',
    },
    squares: {
        id: 'squares',
        type: 'patternSquares',
        size: 4,
        padding: 4,
        stagger: false,
        background: color.transparent.hex,
        color: 'inherit',
    },
    dots2: {
        id: 'dots2',
        type: 'patternDots',
        size: 4,
        padding: 1,
        stagger: true,
        background: color.transparent.hex,
        color: 'inherit',
    },
    lines2: {
        id: 'lines2',
        type: 'patternLines',
        rotation: -45,
        lineWidth: 6,
        spacing: 10,
        background: color.transparent.hex,
        color: 'inherit',
    },
    squares2: {
        id: 'squares2',
        type: 'patternSquares',
        size: 6,
        padding: 1,
        stagger: true,
        background: color.transparent.hex,
        color: 'inherit',
    },
    solid: {
        id: 'solid',
        type: 'patternDots',
        size: 1,
        padding: 0,
        stagger: false,
        background: color.transparent.hex,
        color: 'inherit',
    },
    gray: {
        id: 'gray',
        type: 'patternDots',
        size: 1,
        padding: 1,
        stagger: false,
        background: color.transparent.hex,
        color: 'inherit',
    },
};
// combined theme and component defaults
export const defaults = {
    ...lineDefaults,
    ...pieDefaults,
    ...heatmapDefaults,
    ...barDefaults,
    ...bulletDefaults,
    ...scatterplotDefaults,
};
export const defaultsDark = {
    ...lineDefaultsDark,
    ...pieDefaultsDark,
    ...heatmapDefaultsDark,
    ...barDefaultsDark,
    ...bulletDefaultsDark,
    ...scatterplotDefaultsDark,
};
//# sourceMappingURL=nivo.js.map